/**
 * Dialog (web) — Portal-based overlay using react-dom createPortal.
 *
 * Why not inline fixed-position? Parent transforms/stacking contexts can break
 * position:fixed. Portaling to document.body avoids this entirely.
 *
 * Why not RN Modal? RN Modal creates a separate native view tree that breaks
 * CSS variable cascade (theme tokens stop working).
 *
 * createPortal is the sweet spot:
 *  - Renders at document.body → always on top, no stacking context issues
 *  - Preserves React context (unlike RN Modal) → useThemeName() works
 *  - Re-wrap with <Theme> inside the portal to restore CSS variable cascade
 *  - Always mounted, opacity-toggled → fade in/out via CSS transition
 */
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Pressable } from 'react-native'
import { styled, YStack, Theme, useThemeName } from 'tamagui'

const DialogOverlay = styled(YStack, {
  name: 'DialogOverlay',
  bg: '$backgroundOverlay',
  ai: 'center',
  jc: 'center',
  p: '$lg',
})

const DialogCard = styled(YStack, {
  name: 'DialogCard',
  bg: '$backgroundModal',
  br: '$lg',
  overflow: 'hidden',
  w: '100%',
  maxWidth: 400,
})

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  const themeName = useThemeName()

  return createPortal(
    <Theme name={themeName} forceClassName>
      <DialogOverlay
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.15s ease',
        } as any}
      >
        <Pressable
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          onPress={() => onOpenChange(false)}
        />
        <DialogCard onClick={(e: any) => e.stopPropagation()}>{children}</DialogCard>
      </DialogOverlay>
    </Theme>,
    document.body,
  )
}
